/**
 * Map Debugging Utilities
 *
 * Comprehensive logging for map initialization, bounds calculation, and viewport changes.
 * Can be toggled on/off via localStorage key 'DEBUG_MAP'
 */

 

const DEBUG_KEY = 'DEBUG_MAP';
const COLORS = {
  BOUNDS: '#FF6B6B',
  ZOOM: '#4ECDC4',
  CENTER: '#45B7D1',
  VIEWPORT: '#FFA07A',
  EVENT: '#98D8C8',
  WARNING: '#F7DC6F',
  ERROR: '#EC7063',
};

export function isMapDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(DEBUG_KEY) === 'true';
}

export function enableMapDebug() {
  localStorage.setItem(DEBUG_KEY, 'true');
  console.log('%c🗺️ Map Debug Enabled', `color: ${COLORS.BOUNDS}; font-weight: bold; font-size: 14px`);
}

export function disableMapDebug() {
  localStorage.removeItem(DEBUG_KEY);
  console.log('%c🗺️ Map Debug Disabled', `color: ${COLORS.ERROR}; font-weight: bold; font-size: 14px`);
}

export function logBounds(
  label: string,
  bounds: { ne: { lat: number; lng: number }; sw: { lat: number; lng: number } },
) {
  if (!isMapDebugEnabled()) return;

  const latDelta = bounds.ne.lat - bounds.sw.lat;
  const lngDelta = bounds.ne.lng - bounds.sw.lng;

  console.group(`%c📍 ${label}`, `color: ${COLORS.BOUNDS}; font-weight: bold`);
  console.log('%cNE (Max):', `color: ${COLORS.BOUNDS}`, `lat: ${bounds.ne.lat.toFixed(6)}, lng: ${bounds.ne.lng.toFixed(6)}`);
  console.log('%cSW (Min):', `color: ${COLORS.BOUNDS}`, `lat: ${bounds.sw.lat.toFixed(6)}, lng: ${bounds.sw.lng.toFixed(6)}`);
  console.log('%cDeltas:', `color: ${COLORS.BOUNDS}`, `lat: ${latDelta.toFixed(6)}°, lng: ${lngDelta.toFixed(6)}°`);
  console.groupEnd();
}

export function logViewport(
  label: string,
  viewport: { width: number; height: number },
) {
  if (!isMapDebugEnabled()) return;

  const aspectRatio = (viewport.width / viewport.height).toFixed(2);

  console.group(`%c📐 ${label}`, `color: ${COLORS.VIEWPORT}; font-weight: bold`);
  console.log('%cDimensions:', `color: ${COLORS.VIEWPORT}`, `${viewport.width}px × ${viewport.height}px`);
  console.log('%cAspect Ratio:', `color: ${COLORS.VIEWPORT}`, `${aspectRatio}:1`);
  console.groupEnd();
}

export function logZoom(
  label: string,
  zoom: number,
  details?: Record<string, unknown>,
) {
  if (!isMapDebugEnabled()) return;

  console.group(`%c🔍 ${label}`, `color: ${COLORS.ZOOM}; font-weight: bold`);
  console.log('%cZoom Level:', `color: ${COLORS.ZOOM}`, zoom.toFixed(2));
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.log(`%c${key}:`, `color: ${COLORS.ZOOM}`, value);
    });
  }
  console.groupEnd();
}

export function logCenter(
  label: string,
  center: { lat: number; lng: number },
) {
  if (!isMapDebugEnabled()) return;

  console.log(
    `%c${label}: lat=${center.lat.toFixed(6)}, lng=${center.lng.toFixed(6)}`,
    `color: ${COLORS.CENTER}; font-weight: bold`,
  );
}

export function logMapBounds(
  label: string,
  mapBounds: { getNorthEast: () => { lat: () => number; lng: () => number }; getSouthWest: () => { lat: () => number; lng: () => number } } | null,
) {
  if (!isMapDebugEnabled() || !mapBounds) return;

  const ne = mapBounds.getNorthEast();
  const sw = mapBounds.getSouthWest();

  logBounds(label, {
    ne: { lat: ne.lat(), lng: ne.lng() },
    sw: { lat: sw.lat(), lng: sw.lng() },
  });
}

export function logEvent(
  eventName: string,
  details?: Record<string, unknown>,
) {
  if (!isMapDebugEnabled()) return;

  console.group(`%c📡 EVENT: ${eventName}`, `color: ${COLORS.EVENT}; font-weight: bold`);
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.log(`%c${key}:`, `color: ${COLORS.EVENT}`, value);
    });
  }
  console.groupEnd();
}

export function logWarning(message: string, details?: Record<string, unknown>) {
  if (!isMapDebugEnabled()) return;

  console.group(`%c⚠️ WARNING: ${message}`, `color: ${COLORS.WARNING}; font-weight: bold`);
  if (details) {
    Object.entries(details).forEach(([key, value]) => {
      console.log(`%c${key}:`, `color: ${COLORS.WARNING}`, value);
    });
  }
  console.groupEnd();
}
